// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Portion-preview state machine (ADR-055, founder acceptance 2026-09-04).
 * TWIN MODULE — byte-identical in frontend/src/lib and mobile/src/lib and
 * proven by the same J07 journey tests over the same shared fixture.
 *
 * Invariants it enforces (PREVIEW invariant #12):
 *  - ONE selected serving drives the stepper, the preview nutrition and
 *    the preview ingredients — they can never describe different amounts.
 *  - Every request carries a sequence number; a response may render ONLY
 *    if it answers the LATEST selection (stale/out-of-order responses are
 *    dropped, never rendered).
 *  - Selecting the PLANNED serving again is not a preview: no request is
 *    sent, the planned state is shown (the server would answer with no
 *    change and no token anyway).
 *  - Nothing here mutates the plan: `planned` changes only through
 *    `applySucceeded`, i.e. after the mutation owner persisted a revision.
 *
 * Pure and framework-agnostic: React/RN screens dispatch these functions
 * and render `state`; the network calls live in the screen.
 */
import {
  nextSupportedServings,
  type MealServings,
  type NutritionRangeDetails,
  type PlanChange,
  type PlanProposalResponse,
  type RecipeDetailResponse,
} from "@bitebalance/contracts"

export type PortionPreviewPhase = "planned" | "loading" | "preview" | "error" | "applying" | "applied"
export type PortionPreviewError = "preview" | "apply" | "stale" | "max_conflict"

export interface PortionPreviewData {
  servings: MealServings
  /** The mutation owner's preview (kcal/cost/token) for exactly `servings`. */
  proposal: PlanProposalResponse
  /** The portion owner's scaled ingredients + nutrition for exactly `servings`. */
  portion: NonNullable<RecipeDetailResponse["portion"]>
}

export interface PortionPreviewState {
  /** The authoritative planned serving (from the current plan revision). */
  planned: number
  /** What the user has selected on the stepper. Equals `planned` when no preview is active. */
  selected: number
  /** Monotonic request sequence — only a response for this seq may render. */
  seq: number
  phase: PortionPreviewPhase
  preview: PortionPreviewData | null
  error: PortionPreviewError | null
}

export interface PortionPreviewRequest {
  seq: number
  servings: MealServings
}

export function initialPortionPreview(planned: number): PortionPreviewState {
  return { planned, selected: planned, seq: 0, phase: "planned", preview: null, error: null }
}

/** Stepper affordances derived from the SELECTED serving (never computed elsewhere). */
export function stepTargets(state: PortionPreviewState): { lower: MealServings | null; higher: MealServings | null } {
  return { lower: nextSupportedServings(state.selected, -1), higher: nextSupportedServings(state.selected, 1) }
}

/** Select a serving explicitly. Returns the next state and the request to send (null when none is needed). */
export function selectServings(
  state: PortionPreviewState,
  next: MealServings
): { state: PortionPreviewState; request: PortionPreviewRequest | null } {
  if (state.phase === "applying") return { state, request: null }
  const seq = state.seq + 1 // invalidates every in-flight response
  if (next === state.planned) {
    return { state: { ...state, selected: next, seq, phase: "planned", preview: null, error: null }, request: null }
  }
  return { state: { ...state, selected: next, seq, phase: "loading", preview: null, error: null }, request: { seq, servings: next } }
}

/** Step the selection through the supported model (1 ⇄ 1.5 ⇄ 2). A no-op at the ends. */
export function stepServings(
  state: PortionPreviewState,
  direction: 1 | -1
): { state: PortionPreviewState; request: PortionPreviewRequest | null } {
  const next = nextSupportedServings(state.selected, direction)
  if (next === null) return { state, request: null }
  return selectServings(state, next)
}

/** A preview response arrived. Ignored unless it answers the latest request AND describes the selected serving. */
export function previewArrived(state: PortionPreviewState, seq: number, data: PortionPreviewData): PortionPreviewState {
  if (seq !== state.seq || state.phase !== "loading") return state
  if (data.servings !== state.selected || data.portion.servings !== state.selected) return state
  return { ...state, phase: "preview", preview: data, error: null }
}

/** A preview request failed. Ignored unless it was the latest request. */
export function previewFailed(state: PortionPreviewState, seq: number): PortionPreviewState {
  if (seq !== state.seq || state.phase !== "loading") return state
  return { ...state, phase: "error", preview: null, error: "preview" }
}

/** Cancel / discard: back to the planned serving, nothing sent, in-flight responses invalidated. */
export function cancelPreview(state: PortionPreviewState): PortionPreviewState {
  if (state.phase === "applying") return state
  return { ...state, selected: state.planned, seq: state.seq + 1, phase: "planned", preview: null, error: null }
}

/** The user approved the preview and the apply call is in flight (also a retry after a transient apply error). */
export function applyStarted(state: PortionPreviewState): PortionPreviewState {
  if (!canApply(state)) return state
  return { ...state, phase: "applying", error: null }
}

/** The mutation owner persisted a new revision: the applied serving is now the planned one. */
export function applySucceeded(state: PortionPreviewState, newPlanned: number): PortionPreviewState {
  return { planned: newPlanned, selected: newPlanned, seq: state.seq + 1, phase: "applied", preview: null, error: null }
}

/** The apply was refused. The preview is kept for a retry, except a stale revision which must be reloaded. */
export function applyFailed(state: PortionPreviewState, kind: Exclude<PortionPreviewError, "preview">): PortionPreviewState {
  if (state.phase !== "applying") return state
  return { ...state, phase: "error", error: kind, preview: kind === "stale" ? null : state.preview }
}

/**
 * Whether the approve control may be enabled: a tokened preview, either
 * fresh or after a TRANSIENT apply error (retry). Never after a max-checkout
 * conflict (the owner refused it — the user changes the portion or the
 * maximum) and never while applying.
 */
export function canApply(state: PortionPreviewState): boolean {
  if (state.preview === null || state.preview.proposal.confirmationToken === null) return false
  return state.phase === "preview" || (state.phase === "error" && state.error === "apply")
}

/**
 * True whenever a preview is held — including the error states that keep
 * it (apply error, max-checkout conflict) — so the stepper value, the card
 * and the ingredient list can never describe different amounts while a
 * preview exists. Both clients gate their preview rendering on this.
 */
export function showsPreview(state: PortionPreviewState): boolean {
  return state.preview !== null
}

// ---------------------------------------------------------------------------
// ADR-055 acceptance 2 — blocked previews and honest cost (pure selectors)
// ---------------------------------------------------------------------------

/**
 * Why a held preview cannot be applied. `above`/`below` carry the validator
 * owner's STRUCTURED day figures (never parsed from text, never recomputed
 * on the device); `other` is any other hard failure (allergen, time, …).
 * Null when the preview PASSed validation.
 */
export type PreviewBlock =
  | { kind: "above" | "below"; details: NutritionRangeDetails }
  | { kind: "other"; codes: string[] }

export function previewBlock(state: PortionPreviewState): PreviewBlock | null {
  const proposal = state.preview?.proposal
  if (!proposal || proposal.validation.status !== "FAIL") return null
  const withDetails =
    proposal.validation.failures.find(f => f.details && f.details.subjectId === "day_kcal") ??
    proposal.validation.failures.find(f => f.details)
  if (withDetails?.details) return { kind: withDetails.details.direction, details: withDetails.details }
  return { kind: "other", codes: [...new Set(proposal.validation.failures.map(f => f.code))] }
}

/** The change the preview describes for `mealId` (the first change as a fallback). */
export function previewChange(state: PortionPreviewState, mealId: string): PlanChange | null {
  const changes = state.preview?.proposal.changes ?? []
  return changes.find(c => c.mealId === mealId) ?? changes[0] ?? null
}

/**
 * True when the mutation owner could not estimate the meal's cost
 * (reason code COST_ESTIMATE_UNAVAILABLE) — the client must then render an
 * "unavailable" state and NEVER a monetary figure (€0.00 would mean free).
 */
export function changeCostUnavailable(change: PlanChange | null): boolean {
  return !!change && change.reasonCodes.includes("COST_ESTIMATE_UNAVAILABLE")
}
